import { useState, useEffect } from "react";
import Footer from "./Footer/Footer";
import Header from "./Header/Header";
import Sidebar from "./Sidebar/Sidebar";
import BalanceChart from "./Balancechart";
import { BrowserProvider } from "ethers";
import { Link, useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import {
  useWeb3ModalAccount,
  useWeb3ModalProvider,
} from "@web3modal/ethers/react";
import { formatEther } from "ethers";
import { toast } from "react-hot-toast";
import * as web3 from "@solana/web3.js";

import axios from "axios";

// Import your API service
import apiService from "../core/Service/detail";
import { postMethod } from "../core/service/common.api";

// Import images properly
import dsh_icon_one from "../assets/dsh_icon_one.webp";
import dsh_icon_two from "../assets/dsh_icon_two.webp";
import dsh_icon_three from "../assets/dsh_icon_three.webp";
import dsh_icon_four from "../assets/dsh_icon_four.webp";
import DashboardSkeleton from "./DashboardSkeleton";

const tokenPriceMap = {
  1: "ethereum", // ETH mainnet
  56: "binancecoin", // BNB mainnet
  137: "polygon", // Polygon
  97: "binancecoin", // BNB testnet (use mainnet price)
};

const Dashboard = () => {
  const { walletProvider } = useWeb3ModalProvider();
  const { isConnected, chainId, address } = useWeb3ModalAccount();
  const navigate = useNavigate();

  // State for wallet balance and portfolio data
  const [balance, setBalance] = useState("0");
  const [usdBalance, setUsdBalance] = useState("0");
  const [preferredCurrency, setPreferredCurrency] = useState("usd");
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch wallet balance
  const getWalletBalance = async () => {
    try {
      if (!isConnected || !walletProvider) {
        // Use stored values if not connected
        const savedBalance = localStorage.getItem("balance");
        const savedUsdBalance = localStorage.getItem("balanceUSD");
        if (savedBalance) setBalance(savedBalance);
        if (savedUsdBalance) setUsdBalance(savedUsdBalance);
        return;
      }

      const provider = new BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const userAddress = await signer.getAddress();

      localStorage.setItem("userAddress", userAddress);

      // Get balance in native token
      const balanceWei = await provider.getBalance(userAddress);
      const nativeBalance = formatEther(balanceWei);
      setBalance(nativeBalance);
      localStorage.setItem("balance", nativeBalance);

      // Get USD conversion
      const coinId = tokenPriceMap[chainId] || "ethereum";
      try {
        const response = await fetch(
          `https://api.coingecko.com/api/v3/simple/price?ids=${coinId}&vs_currencies=usd`
        );
        const data = await response.json();

        const usdPrice = data[coinId]?.usd || 0;
        const balanceInUSD = (parseFloat(nativeBalance) * usdPrice).toFixed(2);
        localStorage.setItem("balanceUSD", balanceInUSD);
        setUsdBalance(balanceInUSD);
      } catch (error) {
        console.error("Price fetch error:", error);
        // Fallback price
        const balanceInUSD = (parseFloat(nativeBalance) * 3000).toFixed(2);
        setUsdBalance(balanceInUSD);
      }
    } catch (error) {
      console.error("Balance fetch error:", error);
    }
  };

  // Fetch portfolio data from API
  const fetchPortfolioData = async () => {
    try {
      setLoading(true);

      const walletAddress =
        address ||
        localStorage.getItem("userAddress") ||
        localStorage.getItem("walletAddress");

      console.log(walletAddress, "WalletAddress");

      if (!walletAddress) {
        setPortfolioData(getDefaultDashboardData());
        return;
      }

      const data = {
        apiUrl: apiService.getUserPortfolio,
        payload: { walletAddress },
      };

      const resp = await postMethod(data);

      if (resp.status === true && resp.data) {
        setPortfolioData(resp.data);
      } else {
        toast.error("Failed to fetch portfolio data");
        setPortfolioData(getDefaultDashboardData());
      }
    } catch (error) {
      console.error("Error fetching portfolio:", error);
      setPortfolioData(getDefaultDashboardData());
    } finally {
      setLoading(false);
    }
  };

  // Default data for fallback
  const getDefaultDashboardData = () => {
    return {
      totalPortfolioValue: 0,
      totalReturns: 0,
      assetCount: 0,
      investedAssets: 0,
      assets: [],
    };
  };

  // Calculate dashboard metrics
  const calculateDashboardMetrics = () => {
    if (!portfolioData) {
      return {
        totalEarnings: 0,
        totalAssets: 0,
        investedCount: 0,
        totalPortfolioValue: 0,
      };
    }

    return {
      totalEarnings: portfolioData.totalReturns || 0,
      totalAssets: portfolioData.assetCount || 0,
      investedCount: portfolioData.assets?.length || 0,
      totalPortfolioValue: portfolioData.totalPortfolioValue || 0,
    };
  };

  // Format invested assets for display
  const getInvestedAssets = () => {
    if (!portfolioData?.assets || portfolioData.assets.length === 0) {
      return [];
    }

    return portfolioData.assets.slice(0, 4).map((asset) => ({
      id: asset._id,
      title: asset.fundName,
      subtitle: asset.investmentStrategy || asset.assetClass,
      desc:
        asset.description?.substring(0, 100) + "..." ||
        `Investment in ${asset.fundName}`,
      icon: asset.profileImage || dsh_icon_one,
      img: asset.profileImage || dsh_icon_one,
      details: [
        {
          label: "Current Value",
          value: `$${(asset.currentValue || 0).toLocaleString()}`,
        },
        {
          label: "Returns",
          value: `$${(asset.totalReturns || 0).toLocaleString()}`,
        },
        { label: "Tokens", value: (asset.tokenAmount || 0).toLocaleString() },
        {
          label: "Share",
          value: `${(asset.shareOfPortfolio || 0).toFixed(1)}%`,
        },
      ],
    }));
  };

  const [Address, setAddress] = useState("");

  useEffect(() => {
    const WalletType = localStorage.getItem("WalletType");
    if (WalletType != "solflareWallet") {
      getWalletBalance();
    } else {
      fetchSolBalance(Address);
    }
  }, [isConnected, walletProvider, chainId, Address]);

  const fetchSolBalance = async (address) => {
    try {
      const connection = new web3.Connection("https://api.devnet.solana.com");
      const walletKey = new web3.PublicKey(address);
      const balance = await connection.getBalance(walletKey);
      const nativeBalance = balance / web3.LAMPORTS_PER_SOL;
      localStorage.setItem("balance", nativeBalance);
      const { data } = await axios.get(
        `https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd`
      );
      const usdPrice = data["solana"].usd;
      const balanceInUSD = (parseFloat(nativeBalance) * usdPrice).toFixed(2);
      localStorage.setItem("balanceUSD", balanceInUSD);
    } catch (error) {}
  };

  useEffect(() => {
    const storedAddress = localStorage.getItem("userAddress");
    setAddress(storedAddress);
    if (storedAddress) {
      fetchPortfolioData();
    }
  }, [address]);

  const metrics = calculateDashboardMetrics();
  const investedAssets = getInvestedAssets();

  return (
    <>
      <Header />
      <main className="main_flx">
        <Sidebar />
        <div className="right_wrap pb-4">
          {loading ? (
            // <div className="loading-overlay">
            //   <Oval
            //     color="#292B2C"
            //     secondaryColor="#D9D9D9"
            //     strokeWidth={2}
            //     height={75}
            //     width={75}
            //   />
            // </div>
            <>
              <DashboardSkeleton />
            </>
          ) : (
            <div>
              <h4 className="subheading">Dashboard</h4>

              <div className="dashboard_top_box">
                <div className="row g-4 mx-0 dashboard_top_row">
                  <div className="col-xl-5">
                    <div className="dashboard_box_left">
                      <div className="dashboard_box_flx">
                        <div className="dashboard_box">
                          <img src={dsh_icon_one} alt="Total Balance" />
                          <span>Total Balance</span>
                          <h4>
                            {preferredCurrency === "usd"
                              ? `$${parseFloat(usdBalance).toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}`
                              : `${parseFloat(balance).toFixed(4)} ${
                                  chainId === 56 ? "BNB" : "ETH"
                                }`}
                          </h4>
                          {/* <small>Wallet Balance</small> */}
                        </div>
                        <div className="dashboard_box">
                          <img src={dsh_icon_two} alt="Total Earnings" />
                          <span>Total Earnings</span>
                          <h4>${metrics.totalEarnings.toLocaleString()}</h4>
                          {/* <small>From investments</small> */}
                        </div>
                      </div>
                      <div className="dashboard_box_flx dsh_bx_mt">
                        <div className="dashboard_box">
                          <img src={dsh_icon_three} alt="Total Assets" />
                          <span>Total Assets</span>
                          <h4>{metrics.totalAssets}</h4>
                          {/* <small>In portfolio</small> */}
                        </div>
                        <div className="dashboard_box">
                          <img src={dsh_icon_four} alt="Invested" />
                          <span>Invested Assets</span>
                          <h4>{metrics.investedCount}</h4>
                          {/* <small>Active investments</small> */}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-7 chart_main_col">
                    <BalanceChart
                      walletProvider={walletProvider}
                      isConnected={isConnected}
                      portfolioValue={metrics.totalPortfolioValue}
                      className="chart_main_wrp"
                    />
                  </div>
                </div>
              </div>

              <div className="dashboard_btm">
                <div className="dsh_subtitle_header">
                  <h4 className="dsh_subtitle">Invested Assets</h4>
                </div>

                {loading ? (
                  <div className="loading-assets">
                    <div className="spinner"></div>
                    <p>Loading your investments...</p>
                  </div>
                ) : investedAssets.length > 0 ? (
                  <div className="dsh_inv_grid mt-4">
                    {investedAssets.map((inv) => (
                      <div key={inv.id} className="dsh_inv_card">
                        <div className="dsh_inv_head">
                          <div className="dsh_inv_top">
                            <h4>{inv.title}</h4>
                          </div>
                          <img
                            src={inv.img}
                            alt={inv.title}
                            onError={(e) => {
                              e.target.src = dsh_icon_one;
                            }}
                          />
                          <h6>{inv.subtitle}</h6>
                          <p>{inv.desc}</p>

                          <div className="dsh_inv_card_cnt">
                            <div className="dsh_inv_cd_flx">
                              {inv.details.slice(0, 2).map((d, i) => (
                                <div key={i} className="dsh_inv_cd_lft">
                                  <span>{d.label}</span>
                                  <h4>{d.value}</h4>
                                </div>
                              ))}
                            </div>
                            <div className="dsh_inv_cd_flx mt-3">
                              {inv.details.slice(2, 4).map((d, i) => (
                                <div key={i} className="dsh_inv_cd_lft">
                                  <span>{d.label}</span>
                                  <h4>{d.value}</h4>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <button
                          className="dsh_inv_btn"
                          onClick={() => navigate(`/marketDetails/${inv.id}`)}
                        >
                          <span>More Info</span>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-investments">
                    <div className="empty-investments">
                      <img
                        src={
                          new URL("./../assets/no-invest.png", import.meta.url)
                        }
                        alt="No investments"
                        className="no_inv_img"
                      />
                      <h5 className="no_iv_title">No Investments Yet</h5>
                      <p className="no_iv_cnt">
                        Start building your portfolio by investing in assets
                      </p>
                      <button
                        className="invest-now-btn primary_btn"
                        onClick={() => navigate("/market")}
                      >
                        Browse Assets
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="dsh_footer_wrap">
            <Footer />
          </div>
        </div>
      </main>
    </>
  );
};

export default Dashboard;
